import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, CheckCircle, XCircle, FileText, Lock, Unlock, Eye, X, ZoomIn, CreditCard, User as UserIcon, Shield, Calendar, RefreshCw } from 'lucide-react';
import Layout from '../../components/Layout';
import API_BASE_URL from '../../config/api';
import { useConfirm } from '../../context/ConfirmContext';
import { useAlert } from '../../context/AlertContext';
import AutoRefreshIndicator from '../../components/AutoRefreshIndicator';

// Auto-refresh interval (30 seconds)
const AUTO_REFRESH_INTERVAL = 30000;

const KYCRequests = ({ isEmbedded = false }) => {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [processingId, setProcessingId] = useState(null);
    const [filterStatus, setFilterStatus] = useState('All');
    const [previewUser, setPreviewUser] = useState(null);
    const [activeDocIndex, setActiveDocIndex] = useState(0);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [showRejectionModal, setShowRejectionModal] = useState(false);
    const [rejectionReason, setRejectionReason] = useState('');
    const [pendingAction, setPendingAction] = useState(null); // { id, type, isBundle }

    // Custom modals
    const { showConfirm } = useConfirm();
    const { showAlert } = useAlert();

    // Auto-refresh state
    const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const intervalRef = useRef(null);
    const isMountedRef = useRef(true);

    const fetchRequests = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        else setIsRefreshing(true);

        try {
            const token = localStorage.getItem('authToken');
            const url = new URL(`${API_BASE_URL}/admin/kyc_requests.php`);
            if (fromDate) url.searchParams.append('from', fromDate);
            if (toDate) url.searchParams.append('to', toDate);
            url.searchParams.append('_t', Date.now());

            const response = await fetch(url.toString(), {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success && isMountedRef.current) {
                setRequests(data.data);
                setLastUpdated(new Date());
            }
        } catch (error) {
            console.error("Failed to fetch KYC requests", error);
        } finally {
            if (isMountedRef.current) {
                setLoading(false);
                setIsRefreshing(false);
            }
        }
    }, [fromDate, toDate]);

    // Initial fetch
    useEffect(() => {
        isMountedRef.current = true;
        fetchRequests(false);

        return () => {
            isMountedRef.current = false;
        };
    }, [fetchRequests]);

    // Auto-refresh interval
    useEffect(() => {
        if (autoRefreshEnabled) {
            intervalRef.current = setInterval(() => {
                fetchRequests(true); // Silent refresh
            }, AUTO_REFRESH_INTERVAL);
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [autoRefreshEnabled, fetchRequests]);

    const toggleAutoRefresh = () => setAutoRefreshEnabled(prev => !prev);

    const handleAction = async (targetId, action, isBundle = true) => {
        if (action === 'reject') {
            setPendingAction({ id: targetId, action, isBundle });
            setRejectionReason('');
            setShowRejectionModal(true);
            return;
        }

        const actionTitle = 'Approve KYC';
        const targetText = isBundle ? 'all documents for this user' : 'this document';

        const confirmed = await showConfirm(
            actionTitle,
            `Are you sure you want to approve ${targetText}?`,
            'success'
        );

        if (!confirmed) return;

        processAction(targetId, action, isBundle);
    };

    const processAction = async (targetId, action, isBundle, reason = null) => {
        setProcessingId(targetId);

        try {
            const token = localStorage.getItem('authToken');
            const payload = isBundle ? { user_id: targetId, action, reason } : { kyc_id: targetId, action, reason };

            const response = await fetch(`${API_BASE_URL}/admin/kyc_requests.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });
            const data = await response.json();
            if (data.success) {
                showAlert(data.message, 'success', 'Success');
                if (previewUser) setPreviewUser(null);
                setShowRejectionModal(false);
                fetchRequests(); // Refresh
            } else {
                showAlert(data.message, 'error', 'Error');
            }
        } catch (error) {
            console.error("Failed to process request", error);
            showAlert('Failed to process request', 'error', 'Error');
        } finally {
            setProcessingId(null);
        }
    };

    // Toggle edit access for verified users
    const handleToggleEditAccess = async (userId, currentStatus) => {
        const newStatus = currentStatus === 1 ? 0 : 1;
        const actionText = newStatus === 1 ? 'Grant Edit Access' : 'Revoke Edit Access';
        const actionMessage = newStatus === 1
            ? 'This will allow the user to edit their profile details.'
            : 'This will lock the user\'s profile from being edited.';
        const actionType = newStatus === 1 ? 'success' : 'warning';

        const confirmed = await showConfirm(actionText, actionMessage, actionType);
        if (!confirmed) return;

        setProcessingId(userId);
        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/admin/toggle_edit_access.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ user_id: userId, can_edit: newStatus === 1 })
            });
            const data = await response.json();
            if (data.success) {
                showAlert(data.message, 'success', 'Success');
                fetchRequests(); // Refresh
            } else {
                showAlert(data.message || 'Failed to update edit access', 'error', 'Error');
            }
        } catch (error) {
            console.error("Failed to toggle edit access", error);
            showAlert('Failed to toggle edit access', 'error', 'Error');
        } finally {
            setProcessingId(null);
        }
    };

    const filteredRequests = requests.filter(req => {
        const matchesSearch = (req.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (req.userCode || '').toLowerCase().includes(searchTerm.toLowerCase());
        const status = req.status.toLowerCase();
        const filter = filterStatus.toLowerCase();

        const matchesStatus = filter === 'all' || status === filter;
        return matchesSearch && matchesStatus;
    });

    const Container = isEmbedded ? React.Fragment : Layout;
    const containerProps = isEmbedded ? {} : { title: "KYC Approvals" };

    const openPreview = (user) => {
        setPreviewUser(user);
        setActiveDocIndex(0);
    };

    return (
        <Container {...containerProps}>
            <div className="space-y-6">
                {!isEmbedded && (
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6 bg-gradient-to-br from-white to-gray-50/50 p-8 rounded-[2.5rem] shadow-sm border border-gray-100 mt-2">
                        <div className="flex items-center gap-5">
                            <div className="p-4 bg-indigo-600 rounded-3xl shadow-xl shadow-indigo-100">
                                <Shield className="text-white" size={32} />
                            </div>
                            <div>
                                <h1 className="text-3xl sm:text-4xl font-black text-gray-900 tracking-tight leading-tight">KYC Approvals</h1>
                                <p className="text-gray-500 font-bold mt-1 uppercase text-[10px] tracking-[0.2em] flex items-center gap-2">
                                    <CheckCircle size={12} className="text-emerald-500" />
                                    Identity Verification Dashboard
                                </p>
                            </div>
                        </div>
                        <AutoRefreshIndicator
                            lastUpdated={lastUpdated}
                            isAutoRefreshEnabled={autoRefreshEnabled}
                            onToggle={toggleAutoRefresh}
                            onRefresh={() => fetchRequests(false)}
                            loading={isRefreshing}
                            intervalSeconds={30}
                        />
                    </div>
                )}

                <div className="bg-white rounded-[2rem] p-4 shadow-sm border border-gray-100 flex flex-col xl:flex-row justify-between items-center gap-4">
                    <div className="relative flex-1 w-full">
                        <Search className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                        <input
                            type="text"
                            placeholder="Search by User Code or Name..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-14 pr-4 py-4 bg-gray-50 border-none rounded-3xl focus:ring-2 focus:ring-indigo-500 focus:outline-none font-bold text-sm placeholder:text-gray-400 placeholder:font-medium transition-all"
                        />
                    </div>

                    <div className="flex flex-wrap items-center gap-3 w-full xl:w-auto">
                        <div className="flex items-center gap-2 bg-gray-50 px-4 py-2 rounded-2xl border border-gray-100 cursor-pointer hover:border-indigo-200 transition-colors" onClick={(e) => e.currentTarget.querySelector('input').showPicker()}>
                            <Calendar size={18} className="text-gray-400" />
                            <div className="flex flex-col">
                                <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest leading-none">From Date</span>
                                <input
                                    type="date"
                                    value={fromDate}
                                    onChange={(e) => setFromDate(e.target.value)}
                                    className="bg-transparent text-xs font-black focus:outline-none cursor-pointer text-gray-700"
                                />
                            </div>
                        </div>
                        <div className="flex items-center gap-2 bg-gray-50 px-4 py-2 rounded-2xl border border-gray-100 cursor-pointer hover:border-indigo-200 transition-colors" onClick={(e) => e.currentTarget.querySelector('input').showPicker()}>
                            <Calendar size={18} className="text-gray-400" />
                            <div className="flex flex-col">
                                <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest leading-none">To Date</span>
                                <input
                                    type="date"
                                    value={toDate}
                                    onChange={(e) => setToDate(e.target.value)}
                                    className="bg-transparent text-xs font-black focus:outline-none cursor-pointer text-gray-700"
                                />
                            </div>
                        </div>

                        <select
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                            className="px-6 py-4 border-none border-gray-100 rounded-3xl focus:ring-2 focus:ring-indigo-500 focus:outline-none bg-gray-50 text-gray-700 font-bold text-sm shadow-inner transition-all appearance-none cursor-pointer min-w-[180px]"
                        >
                            <option value="All">All Statuses</option>
                            <option value="Pending">Pending</option>
                            <option value="Approved">Approved</option>
                            <option value="Rejected">Rejected</option>
                        </select>
                        <button
                            onClick={() => fetchRequests()}
                            className="px-8 py-4 bg-gray-900 text-white text-xs font-black uppercase tracking-[0.2em] rounded-3xl hover:bg-black transition-all shadow-xl shadow-gray-200 flex items-center justify-center gap-3 active:scale-95 w-full sm:w-auto"
                        >
                            <RefreshCw size={16} className={isRefreshing ? 'animate-spin' : ''} />
                            Apply Filters
                        </button>
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    <>
                        <div className="hidden lg:block overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-50/50 border-b border-gray-100 text-gray-400 text-[10px] font-black uppercase tracking-[0.2em]">
                                    <tr>
                                        <th className="px-8 py-6 text-left">Growth Partner</th>
                                        <th className="px-8 py-6 text-left">Verification Docs</th>
                                        <th className="px-8 py-6 text-left">Latest Timeline</th>
                                        <th className="px-8 py-6 text-center">Current Status</th>
                                        <th className="px-8 py-6 text-center">Profile Edit</th>
                                        <th className="px-8 py-6 text-center">Moderation</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {loading ? (
                                        <tr key="loading"><td colSpan="6" className="p-8 text-center text-gray-500">Loading...</td></tr>
                                    ) : filteredRequests.length === 0 ? (
                                        <tr key="empty"><td colSpan="6" className="p-8 text-center text-gray-500">No requests found.</td></tr>
                                    ) : (
                                        filteredRequests.map((req) => {
                                            const status = (req.status || 'Pending').toLowerCase();
                                            return (
                                                <tr key={req.userId} className="hover:bg-gray-50/50 transition-colors group border-b border-gray-50 last:border-none">
                                                    <td className="px-8 py-6">
                                                        <div className="flex items-center gap-4">
                                                            <div className="w-12 h-12 bg-gray-100 rounded-2xl flex items-center justify-center text-gray-400 font-black group-hover:bg-indigo-600 group-hover:text-white transition-all shadow-sm">
                                                                {(req.name || 'U').charAt(0)}
                                                            </div>
                                                            <div className="flex flex-col">
                                                                <span className="text-sm font-black text-gray-900 group-hover:text-indigo-600 transition-colors uppercase tracking-tight">{req.name}</span>
                                                                <span className="text-[10px] font-bold text-gray-400 font-mono mt-0.5">{req.userCode}</span>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-8 py-6 text-gray-600 font-medium">
                                                        <div className="flex items-center gap-2">
                                                            <div className="flex -space-x-2">
                                                                {[1, 2].map(i => (
                                                                    <div key={i} className="w-8 h-8 rounded-lg bg-gray-100 border-2 border-white flex items-center justify-center">
                                                                        <FileText size={14} className="text-gray-400" />
                                                                    </div>
                                                                ))}
                                                            </div>
                                                            <span className="text-[10px] font-black text-gray-400 uppercase ml-2 tracking-widest">Documents</span>
                                                        </div>
                                                    </td>
                                                    <td className="px-8 py-6">
                                                        <div className="flex flex-col">
                                                            <span className="text-sm font-black text-gray-900 tracking-tighter">{req.requestedAt || '-'}</span>
                                                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Updated</span>
                                                        </div>
                                                    </td>
                                                    <td className="px-8 py-6 text-center">
                                                        <span className={`px-5 py-2 rounded-xl text-[10px] font-black tracking-[0.2em] uppercase shadow-sm border ${status === 'approved' ? 'bg-green-100 text-green-700 border-green-200' :
                                                            status === 'rejected' ? 'bg-red-100 text-red-700 border-red-200' :
                                                                'bg-amber-100 text-amber-700 border-amber-200'
                                                            }`}>
                                                            {status}
                                                        </span>
                                                    </td>
                                                    <td className="px-8 py-6 text-center">
                                                        <button
                                                            onClick={() => handleToggleEditAccess(req.userId, req.canEditProfile)}
                                                            className={`p-3 rounded-2xl transition-all shadow-sm active:scale-95 ${req.canEditProfile === 1
                                                                ? 'bg-green-50 text-green-600 hover:bg-green-100 ring-2 ring-green-100'
                                                                : 'bg-red-50 text-red-600 hover:bg-red-100 ring-2 ring-red-100'
                                                                }`}
                                                            title={req.canEditProfile === 1 ? 'Revoke Profile Edit' : 'Grant Profile Edit'}
                                                        >
                                                            {req.canEditProfile === 1 ? <Unlock size={18} /> : <Lock size={18} />}
                                                        </button>
                                                    </td>
                                                    <td className="px-8 py-6 text-center">
                                                        <button
                                                            onClick={() => openPreview(req)}
                                                            className="inline-flex items-center gap-2 bg-gray-900 text-white px-6 py-3 rounded-2xl hover:bg-black transition-all font-black text-[10px] uppercase tracking-[0.2em] shadow-lg shadow-gray-200 active:scale-95"
                                                        >
                                                            <Eye size={16} /> Moderation
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile View */}
                        <div className="lg:hidden divide-y divide-gray-100">
                            {loading ? (
                                <div className="p-12 text-center">
                                    <div className="w-10 h-10 border-4 border-indigo-100 border-t-indigo-600 rounded-full animate-spin mx-auto mb-4"></div>
                                    <p className="text-gray-400 font-bold uppercase text-[10px] tracking-widest">Loading Requests...</p>
                                </div>
                            ) : filteredRequests.length === 0 ? (
                                <div className="p-20 text-center">
                                    <Shield className="mx-auto text-gray-200 mb-4" size={48} />
                                    <p className="text-gray-400 font-bold uppercase text-[10px] tracking-widest">No pending verifications</p>
                                </div>
                            ) : (
                                filteredRequests.map((req) => {
                                    const status = (req.status || 'Pending').toLowerCase();
                                    return (
                                        <div key={req.userId} className="p-6 flex flex-col gap-5 hover:bg-gray-50/50 transition-colors border-b border-gray-50 last:border-none">
                                            <div className="flex justify-between items-start">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-12 h-12 bg-gray-100 rounded-2xl flex items-center justify-center text-gray-400 font-black shadow-sm border border-white">
                                                        {(req.name || 'U').charAt(0)}
                                                    </div>
                                                    <div>
                                                        <div className="text-sm font-black text-gray-900 uppercase tracking-tight">{req.name}</div>
                                                        <div className="text-[10px] text-gray-400 font-mono tracking-tighter mt-0.5">{req.userCode}</div>
                                                    </div>
                                                </div>
                                                <span className={`px-4 py-1.5 rounded-xl text-[9px] font-black tracking-widest uppercase shadow-sm border ${status === 'approved' ? 'bg-green-100 text-green-700 border-green-200' :
                                                    status === 'rejected' ? 'bg-red-100 text-red-700 border-red-200' :
                                                        'bg-amber-100 text-amber-700 border-amber-200'
                                                    }`}>
                                                    {status}
                                                </span>
                                            </div>

                                            <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-inner flex items-center justify-between">
                                                <div className="flex flex-col">
                                                    <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1">Last Update</span>
                                                    <span className="text-[11px] font-bold text-gray-700">{req.requestedAt || '-'}</span>
                                                </div>
                                                <div className="flex -space-x-2">
                                                    {[1, 2].map(i => (
                                                        <div key={i} className="w-8 h-8 rounded-lg bg-gray-50 border-2 border-white flex items-center justify-center shadow-sm">
                                                            <FileText size={14} className="text-gray-400" />
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-3">
                                                <button
                                                    onClick={() => handleToggleEditAccess(req.userId, req.canEditProfile === 1 ? 0 : 1)}
                                                    className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl transition-all font-black text-[10px] uppercase tracking-widest border ${req.canEditProfile === 1
                                                        ? 'bg-green-50 text-green-600 border-green-100'
                                                        : 'bg-red-50 text-red-600 border-red-100'
                                                        }`}
                                                >
                                                    {req.canEditProfile === 1 ? <Unlock size={14} /> : <Lock size={14} />}
                                                    Edit Access
                                                </button>
                                                <button
                                                    onClick={() => openPreview(req)}
                                                    className="flex-[1.5] flex items-center justify-center gap-3 bg-gray-900 text-white py-3 rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-xl shadow-gray-200 active:scale-95"
                                                >
                                                    <Eye size={16} /> Moderation
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </>
                </div >
            </div >

            {/* Comprehensive Document Preview Modal */}
            {
                previewUser && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-75 backdrop-blur-sm">
                        <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full overflow-hidden animate-in zoom-in duration-200 flex flex-col max-h-[90vh]">
                            {/* Header */}
                            <div className="px-6 py-4 flex justify-between items-center border-b border-gray-100 bg-gray-50 shrink-0">
                                <div>
                                    <h3 className="font-bold text-gray-900 flex items-center gap-2">
                                        <Shield className="text-blue-600" size={20} />
                                        KYC Verification Panel
                                    </h3>
                                    <div className="flex items-center gap-3 mt-1">
                                        <span className="text-xs font-semibold text-gray-600 bg-gray-200 px-2 py-0.5 rounded flex items-center gap-1">
                                            <UserIcon size={12} /> {previewUser.name}
                                        </span>
                                        <span className="text-xs font-mono text-blue-600 bg-blue-50 px-2 py-0.5 rounded leading-none">
                                            {previewUser.userCode}
                                        </span>
                                    </div>
                                </div>
                                <button onClick={() => setPreviewUser(null)} className="p-2 hover:bg-gray-200 rounded-lg transition-colors text-gray-500">
                                    <X size={20} />
                                </button>
                            </div>

                            {/* Content Body */}
                            <div className="flex flex-1 flex-col lg:flex-row overflow-hidden pointer-events-auto">
                                {/* Left Sidebar: Document Switcher */}
                                <div className="w-full lg:w-1/4 border-b lg:border-r lg:border-b-0 border-gray-100 p-4 bg-gray-50/50 space-y-3 overflow-y-auto max-h-[200px] lg:max-h-full">
                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-2">Click to View Documents</p>
                                    {previewUser.documents.map((doc, idx) => (
                                        <button
                                            key={idx}
                                            onClick={() => setActiveDocIndex(idx)}
                                            className={`w-full text-left p-3 rounded-xl transition-all border ${activeDocIndex === idx
                                                ? 'bg-blue-600 border-blue-600 shadow-lg shadow-blue-100'
                                                : 'bg-white border-gray-200 hover:border-blue-300'
                                                }`}
                                        >
                                            <div className="flex items-center gap-3">
                                                <div className={`p-2 rounded-lg ${activeDocIndex === idx ? 'bg-blue-500 text-white' : 'bg-blue-50 text-blue-600'}`}>
                                                    <CreditCard size={18} />
                                                </div>
                                                <div>
                                                    <p className={`text-sm font-bold capitalize ${activeDocIndex === idx ? 'text-white' : 'text-gray-900'}`}>{doc.type}</p>
                                                    <p className={`text-[10px] ${activeDocIndex === idx ? 'text-blue-100' : 'text-gray-500'}`}>
                                                        {doc.status}
                                                    </p>
                                                </div>
                                            </div>
                                        </button>
                                    ))}

                                    <div className="hidden lg:block pt-6 mt-6 border-t border-gray-200">
                                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-2 mb-3">Overall Result</p>
                                        <div className="space-y-2">
                                            <button
                                                onClick={() => handleAction(previewUser.userId, 'approve', true)}
                                                disabled={processingId === previewUser.userId}
                                                className="w-full flex items-center justify-center gap-2 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-all font-bold shadow-lg shadow-green-100 disabled:opacity-50"
                                            >
                                                <CheckCircle size={18} /> Approve All
                                            </button>
                                            <button
                                                onClick={() => handleAction(previewUser.userId, 'reject', true)}
                                                disabled={processingId === previewUser.userId}
                                                className="w-full flex items-center justify-center gap-2 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-all font-bold shadow-lg shadow-red-100 disabled:opacity-50"
                                            >
                                                <XCircle size={18} /> Reject All
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Main Preview Area */}
                                <div className="flex-1 flex flex-col md:flex-row p-6 gap-6 overflow-y-auto bg-white">
                                    {/* Doc Details */}
                                    <div className="w-full md:w-1/3 space-y-5">
                                        <div className="bg-gray-50 p-4 rounded-2xl border border-gray-100">
                                            <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">ID Details</label>
                                            <div className="space-y-4">
                                                <div>
                                                    <p className="text-xs text-gray-500">Document Type</p>
                                                    <p className="text-lg font-bold text-gray-900 capitalize italic">{previewUser.documents[activeDocIndex].type}</p>
                                                </div>
                                                <div>
                                                    <p className="text-xs text-gray-500">Identity Number</p>
                                                    <p className="text-xl font-mono font-black text-blue-700 bg-blue-50 py-1 rounded inline-block">
                                                        {previewUser.documents[activeDocIndex].docNumber}
                                                    </p>
                                                </div>
                                                <div>
                                                    <p className="text-xs text-gray-500">Current Status</p>
                                                    <div className="flex flex-wrap items-center gap-2 mt-1">
                                                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${(previewUser.documents[activeDocIndex].status || '').toLowerCase() === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                                                            (previewUser.documents[activeDocIndex].status || '').toLowerCase() === 'approved' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                                            }`}>
                                                            {previewUser.documents[activeDocIndex].status}
                                                        </span>
                                                        <div className="flex gap-1 ml-auto">
                                                            <button
                                                                onClick={() => handleAction(previewUser.documents[activeDocIndex].id, 'approve', false)}
                                                                disabled={processingId === previewUser.documents[activeDocIndex].id}
                                                                className="p-1.5 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-all border border-green-200"
                                                                title="Approve Document"
                                                            >
                                                                <CheckCircle size={14} />
                                                            </button>
                                                            <button
                                                                onClick={() => handleAction(previewUser.documents[activeDocIndex].id, 'reject', false)}
                                                                disabled={processingId === previewUser.documents[activeDocIndex].id}
                                                                className="p-1.5 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-all border border-red-200"
                                                                title="Reject Document"
                                                            >
                                                                <XCircle size={14} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Bank Details Block */}
                                        <div className="bg-blue-50 p-4 rounded-2xl border border-blue-100">
                                            <label className="block text-xs font-bold text-blue-400 uppercase tracking-widest mb-2">User Bank Details</label>
                                            <div className="space-y-3 text-sm">
                                                <div className="flex justify-between border-b border-blue-100 pb-1">
                                                    <span className="text-gray-500 text-xs">Bank Name</span>
                                                    <span className="font-bold text-gray-900">{previewUser.bankDetails?.bankName || 'N/A'}</span>
                                                </div>
                                                <div className="flex justify-between border-b border-blue-100 pb-1">
                                                    <span className="text-gray-500 text-xs">Account No.</span>
                                                    <span className="font-bold text-gray-900 font-mono tracking-wide flex items-center gap-1">
                                                        {previewUser.bankDetails?.accountNumber || 'N/A'}
                                                        {previewUser.bankDetails?.passbookAccountNumber && previewUser.bankDetails?.accountNumber === previewUser.bankDetails?.passbookAccountNumber && (
                                                            <span className="text-[8px] bg-amber-100 text-amber-700 px-1 rounded font-bold">passbook</span>
                                                        )}
                                                    </span>
                                                </div>
                                                <div className="flex justify-between border-b border-blue-100 pb-1">
                                                    <span className="text-gray-500 text-xs">IFSC Code</span>
                                                    <span className="font-bold text-gray-900 font-mono">{previewUser.bankDetails?.ifscCode || 'N/A'}</span>
                                                </div>
                                                <div className="flex justify-between border-b border-blue-100 pb-1">
                                                    <span className="text-gray-500 text-xs">Branch Name</span>
                                                    <span className="font-bold text-gray-900">{previewUser.bankDetails?.branchName || 'N/A'}</span>
                                                </div>
                                                <div className="flex justify-between border-b border-blue-100 pb-1">
                                                    <span className="text-gray-500 text-xs">Acc. Type</span>
                                                    <span className="font-bold text-gray-900 uppercase">{previewUser.bankDetails?.accountType || 'N/A'}</span>
                                                </div>
                                                <div className="flex justify-between border-b border-blue-100 pb-1">
                                                    <span className="text-gray-500 text-xs">PAN Number</span>
                                                    <span className="font-bold text-gray-900 font-mono">{previewUser.bankDetails?.panNumber || 'N/A'}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-gray-500 text-xs">Acc. Holder</span>
                                                    <span className="font-bold text-gray-900 text-right">{previewUser.bankDetails?.accountHolder || 'N/A'}</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="bg-amber-50 p-4 rounded-2xl border border-amber-100">
                                            <p className="text-xs text-amber-800 font-bold mb-1 italic">Admin Reminder:</p>
                                            <p className="text-xs text-amber-700 leading-relaxed">
                                                Verify the ID number strictly against the uploaded image. Check for clarity and tampering.
                                            </p>
                                        </div>
                                    </div>

                                    {/* Doc View */}
                                    <div className="flex-1 flex flex-col">
                                        <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Document Image</label>
                                        <div className="flex-1 relative group bg-gray-100 rounded-2xl overflow-hidden border-2 border-dashed border-gray-200 flex items-center justify-center min-h-[400px]">
                                            {!previewUser.documents[activeDocIndex].file ? (
                                                <div className="text-center p-6">
                                                    <FileText size={48} className="mx-auto text-gray-300 mb-2" />
                                                    <p className="text-sm text-gray-400">No document image uploaded</p>
                                                </div>
                                            ) : previewUser.documents[activeDocIndex].file.toLowerCase().endsWith('.pdf') ? (
                                                <iframe
                                                    src={`${previewUser.documents[activeDocIndex].file}#toolbar=0`}
                                                    className="w-full h-full border-none"
                                                    title="PDF Preview"
                                                />
                                            ) : (
                                                <img
                                                    src={previewUser.documents[activeDocIndex].file}
                                                    alt="Document Preview"
                                                    className="w-full h-full object-contain"
                                                    onError={(e) => {
                                                        e.target.onerror = null;
                                                        e.target.src = 'https://via.placeholder.com/600x400?text=Document+Image+Not+Found';
                                                    }}
                                                />
                                            )}
                                            <div className="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                <a
                                                    href={previewUser.documents[activeDocIndex].file}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="p-3 bg-black/50 hover:bg-black/70 text-white rounded-full backdrop-blur-sm transition-all"
                                                    title="Full Resolution"
                                                >
                                                    <ZoomIn size={20} />
                                                </a>
                                                <a
                                                    href={previewUser.documents[activeDocIndex].file}
                                                    download
                                                    className="p-3 bg-black/50 hover:bg-black/70 text-white rounded-full backdrop-blur-sm transition-all"
                                                    title="Download File"
                                                >
                                                    <FileText size={20} />
                                                </a>
                                            </div>
                                        </div>
                                        <div className="mt-3 flex justify-center gap-4 text-[10px] text-gray-500 font-medium italic">
                                            <span>Uploaded: {previewUser.documents[activeDocIndex].date}</span>
                                            <span>•</span>
                                            <button className="hover:text-blue-600 transition-colors">Rotate View</button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Mobile Actions Footer */}
                            <div className="lg:hidden p-4 border-t border-gray-100 bg-white grid grid-cols-2 gap-3">
                                <button
                                    onClick={() => handleAction(previewUser.userId, 'approve', true)}
                                    disabled={processingId === previewUser.userId}
                                    className="flex items-center justify-center gap-2 py-3 bg-green-600 text-white rounded-xl font-bold shadow-lg shadow-green-100"
                                >
                                    <CheckCircle size={16} /> Approve All
                                </button>
                                <button
                                    onClick={() => handleAction(previewUser.userId, 'reject', true)}
                                    disabled={processingId === previewUser.userId}
                                    className="flex items-center justify-center gap-2 py-3 bg-red-600 text-white rounded-xl font-bold shadow-lg shadow-red-100"
                                >
                                    <XCircle size={16} /> Reject All
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Rejection Reason Modal */}
            {showRejectionModal && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
                    <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-md overflow-hidden animate-scale-in border border-gray-100">
                        <div className="p-6 text-white bg-red-600">
                            <h2 className="text-xl font-black uppercase tracking-widest flex items-center gap-2">
                                <XCircle size={24} />
                                REJECT KYC
                            </h2>
                            <p className="text-[10px] font-bold opacity-80 mt-1 uppercase tracking-widest">Provide a reason for rejection</p>
                        </div>

                        <div className="p-8 space-y-6">
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Rejection Reason</label>
                                <textarea
                                    value={rejectionReason}
                                    onChange={(e) => setRejectionReason(e.target.value)}
                                    placeholder="Examples: Documents not clear, Name mismatch, Invalid ID number..."
                                    className="w-full bg-gray-50 border-2 border-gray-100 rounded-2xl p-4 text-sm font-bold text-gray-700 focus:outline-none focus:border-red-500 focus:ring-4 focus:ring-red-50/50 transition-all min-h-[120px]"
                                />
                            </div>

                            <div className="flex gap-4">
                                <button
                                    onClick={() => setShowRejectionModal(false)}
                                    className="flex-1 py-4 text-xs font-black uppercase tracking-widest text-gray-400 hover:text-gray-600 bg-gray-50 hover:bg-gray-100 rounded-xl transition-all"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => processAction(pendingAction.id, 'reject', pendingAction.isBundle, rejectionReason)}
                                    disabled={processingId || !rejectionReason.trim()}
                                    className="flex-1 py-4 text-xs font-black uppercase tracking-widest text-white rounded-xl shadow-lg transition-all flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 shadow-red-100 disabled:opacity-50"
                                >
                                    {processingId ? <RefreshCw className="animate-spin" size={16} /> : "REJECT NOW"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                .animate-fade-in { animation: fadeIn 0.3s ease-out; }
                .animate-scale-in { animation: scaleIn 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275); }
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
                @keyframes scaleIn { from { transform: scale(0.95); opacity: 0; } to { transform: scale(1); opacity: 1; } }
            `}</style>
        </Container>
    );
};

export default KYCRequests;





